/**
 * Audiences list view — /w/:slug/audiences (flag-gated by RequireAudiences).
 *
 * The shared list-page pattern (`useListControls` + `ListToolbar` + `VirtualizedCards` / `Table`)
 * applied to audiences — the same surface as Dashboards / Workflows / Context: search, a card/list
 * view toggle, and sort. Only saved audiences appear here (ACT2-583); drafts stay reachable from the
 * chat that created them. There is no "New audience" button anywhere — audiences are created in chat,
 * so the empty state points there. The list is read-only for counts: each Size shows the persisted
 * count, or "Unknown" when none has been run — running a count lives on the detail page. Delete
 * (confirmed) is available per-row to the creator or an admin — the same gate as the detail page.
 *
 * ACT2-640: each row/card shows a warehouse-TYPE logo beside the title (name on hover / search) and
 * an activation count that hides at zero. `activationCount` rides `audience.list`; warehouse type is
 * joined client-side from `settings.warehouses.list`.
 */
import { connectionDisplayLabel, isLookoutMintedAccountName } from '@server/warehouse-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Users } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ConfirmDialog } from '@/_shared/components/ConfirmDialog';
import { ListEmpty, ListError, ListNoMatches, ListSkeleton } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { VirtualizedCards } from '@/_shared/components/list/VirtualizedCards';
import { Table } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useAuth } from '@/_shared/hooks/useAuth';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import {
  useMissingRudderstackGrant,
  useRudderstackGrantsDecide,
  useSiblingActionsOff,
} from '@/_shared/hooks/useSiblingStatus';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';

import { AudienceCard } from '../components/AudienceCard';
import { buildAudienceColumns } from '../components/audienceColumns';
import { type AudienceListRecord, statusOf, toAudienceListItem } from '../lib/audience-view';
import {
  AUDIENCE_DEFAULT_SORT,
  AUDIENCE_DERIVE_CONFIG,
  AUDIENCE_SORT_KEYS,
  AUDIENCE_SORT_OPTIONS,
  type AudienceSortKey,
} from '../lib/audienceList';
import type { AudienceListItem } from '../types';

/**
 * Canonical warehouse type from a `TYPE|host|db` account key; '' when the key is malformed.
 *
 * It only feeds `connectionDisplayLabel`'s fallback for a connection with no database dim. The
 * redacted connection row the settings RPC returns carries no `type`, so the account key's own
 * prefix is the type here — the same derivation the routine picker makes in `useWarehouseOptions`.
 */
function typeFromAccountKey(accountKey: string): string {
  return accountKey.split('|')[0] ?? '';
}

export function AudienceList(): React.ReactElement {
  usePageTitle('Audiences');
  const { wp, isAdmin } = useWorkspace();
  const { user } = useAuth();
  const orpc = useOrpc();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery(orpc.audience.list.queryOptions({ input: {} }));
  const { data: warehouseData } = useQuery(orpc.settings.warehouses.list.queryOptions({ input: {} }));

  /**
   * Label + type per warehouse id, in BOTH spellings an `audiences.warehouse_id` can hold.
   *
   * `create_audience` stores the addressable id (D2): the destination id when the connection
   * resolves one, otherwise the connection's own uuid — which is every audience created on a
   * hand-typed, destination-less connection. Built from the synced `warehouses` rows alone, the map
   * missed exactly those, and a uuid fallback put a bare id into search, hover, and the accessible
   * name. The audience was then findable only by typing a uuid nobody has, and a screen reader heard
   * that uuid. An unresolved id now leaves the label empty. Hover and the accessible name use the
   * type label instead.
   *
   * Each connection's label is its bound account's name (unless Lookout-minted), then its
   * destination's own name, then the database dim. A bound account's label also wins under the
   * destination-id spelling (decision F); otherwise the destination's own name keeps that key.
   */
  const warehouseById = useMemo(() => {
    const map = new Map<string, { label: string; type: string }>();
    for (const w of warehouseData?.warehouses ?? []) {
      map.set(w.rudderDestinationId, { label: w.name, type: w.type });
    }
    for (const c of warehouseData?.connections ?? []) {
      if (!c.id) continue;
      const type = typeFromAccountKey(c.accountKey);
      const destination = c.rudderDestinationId ? map.get(c.rudderDestinationId) : undefined;
      // Never the host dim (R1) — `connectionDisplayLabel` reads the database dim only.
      const label = connectionDisplayLabel({
        rsAccountName: c.rsAccountName,
        destinationName: destination?.label,
        accountKey: c.accountKey,
        type,
      });
      map.set(c.id, { label, type });
      // A bound connection's account label also wins under the destination-id spelling (decision F).
      if (c.rudderDestinationId && c.rsAccountName?.trim() && !isLookoutMintedAccountName(c.rsAccountName)) {
        map.set(c.rudderDestinationId, { label, type: destination?.type ?? type });
      }
    }
    return map;
  }, [warehouseData?.warehouses, warehouseData?.connections]);

  // Read-only projection of the server rows — the list never runs a count, so there are no local
  // overrides to reconcile; whatever the server persists (a count, or "not-run" → "Unknown") is shown.
  // Drafts are chat artifacts, not catalog rows: keep them off this page even though `audience.list`
  // still returns them (the agent and the detail page still need the full set).
  const items = useMemo<AudienceListItem[]>(() => {
    const rows: AudienceListRecord[] = data?.audiences ?? [];
    return rows
      .filter((row) => statusOf(row.status) === 'saved')
      .map((row) => {
        const warehouse = warehouseById.get(row.warehouseId);
        return toAudienceListItem(row, {
          // No uuid fallback: hover and the accessible name use the type label when this is empty.
          label: warehouse?.label ?? '',
          type: warehouse?.type ?? '',
        });
      });
  }, [data?.audiences, warehouseById]);

  // ── Controls (search + view + sort) ──
  const controls = useListControls<AudienceSortKey>({
    sortKeys: AUDIENCE_SORT_KEYS,
    defaultSort: AUDIENCE_DEFAULT_SORT,
    // Bumped from `audiences` deliberately. A stored view preference BEATS `defaultView`, and nothing in
    // the product resets it — `clearAll` leaves `view` alone on purpose — so anyone who had ever touched
    // the view toggle would never have seen the table, headers and all. Which is everyone who has used
    // this page more than once, and every developer reviewing the change. A new key orphans the old
    // record and lets the default apply once.
    //
    // The cost is that a stored SORT goes with it. That is the whole price: one preference, reset once,
    // against a layout nobody would otherwise be shown.
    persistKey: 'audiences.v2',
    // The table is the default now: an audience is read by comparison down a column, and a column needs
    // a header to be worth comparing.
    defaultView: 'list',
  });
  // `controls.query` is already debounced by the toolbar's search box, so this recomputes on the debounce,
  // not per keystroke.
  const visible = useMemo(
    () =>
      filterSortItems(
        items,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        AUDIENCE_DERIVE_CONFIG,
      ),
    [items, controls.query, controls.sort, controls.filters],
  );

  // ── Delete (creator or admin, confirmed) — same gate as the detail page's ⋯ menu ──
  // ACT2-1124 LLD §6 (F13a): in a sibling workspace RudderStack's grants decide the delete of a synced audience; a
  // never-synced one keeps the creator-or-admin rule, as on the detail page.
  const grantsDecide = useRudderstackGrantsDecide();
  const canDeleteItem = useCallback(
    (item: AudienceListItem) => (grantsDecide && item.rsSourceId != null) || isAdmin || item.createdBy === user?.id,
    [grantsDecide, isAdmin, user?.id],
  );
  // ACT2-1124 LLD §5.9: a disabled or gone sibling turns audience actions off. Delete calls RudderStack, so it stays
  // in the menu but is disabled, with the banner's text as the reason, as on the detail page. LLD §6: the delete of a
  // synced audience also needs the person's "Create and delete audiences" grant.
  const siblingActions = useSiblingActionsOff();
  const missingDeleteGrant = useMissingRudderstackGrant('audiences_create_delete');
  const deleteDisabledReason = useCallback(
    (item: AudienceListItem): string | undefined => {
      if (siblingActions.off) return siblingActions.reason;
      if (grantsDecide && item.rsSourceId != null && missingDeleteGrant !== null) return missingDeleteGrant;
      return undefined;
    },
    [siblingActions.off, siblingActions.reason, grantsDecide, missingDeleteGrant],
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Only resolved while the dialog is open — otherwise this scans the whole list on every render.
  const deletingAudience = deletingId ? (items.find((a) => a.id === deletingId) ?? null) : null;
  const requestDelete = useCallback((id: string) => setDeletingId(id), []);
  const deleteMutation = useMutation(
    orpc.audience.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: orpc.audience.list.key() });
        toast.success('Audience deleted');
        setDeletingId(null);
      },
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete audience'),
    }),
  );

  // Columns are display-only apart from the Delete kebab — the list never runs a count.
  const columns = useMemo(
    () => buildAudienceColumns({ onDelete: requestDelete, canDelete: canDeleteItem, deleteDisabledReason }),
    [requestDelete, canDeleteItem, deleteDisabledReason],
  );

  const getItemKey = useCallback((item: AudienceListItem) => item.id, []);
  const openAudience = useCallback((item: AudienceListItem) => navigate(wp(`/audiences/${item.id}`)), [navigate, wp]);
  const renderCard = useCallback(
    (item: AudienceListItem) => (
      <AudienceCard
        audience={item}
        to={wp(`/audiences/${item.id}`)}
        onDelete={canDeleteItem(item) ? requestDelete : undefined}
        deleteDisabledReason={deleteDisabledReason(item)}
      />
    ),
    [wp, canDeleteItem, requestDelete, deleteDisabledReason],
  );

  const hasAudiences = items.length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <header className="border-b px-6 py-4">
        <h1 className="text-lg font-semibold tracking-tight text-foreground">Audiences</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Named, re-countable sets of entities — built and refined from chat.
        </p>
      </header>

      {error && !data ? (
        // Only take over the whole page when there's nothing to show — a failed background refetch keeps
        // the cached list usable, so don't blank it behind a full-screen error.
        <ListError
          title="Couldn't load audiences"
          message={error instanceof Error ? error.message : 'Something went wrong.'}
        >
          {/* Without an action this state is a dead end — the page is the only route to an audience, so a
              failed load leaves a reload as the sole way forward. `refetch` retries just this query. */}
          <Button variant="secondary" onClick={() => void refetch()} Icon={<RefreshCw />} label="Retry" />
        </ListError>
      ) : isLoading ? (
        <ListSkeleton view={controls.view} />
      ) : !hasAudiences ? (
        <ListEmpty
          icon={Users}
          title="No audiences yet"
          description="Created from chat — ask Lookout for an audience to add one."
        />
      ) : (
        <>
          <div className="pt-4 pb-1">
            <div className="mx-auto max-w-5xl px-6">
              <ListToolbar
                query={controls.query}
                onQueryChange={controls.setQuery}
                searchPlaceholder="Search audiences…"
                view={controls.view}
                onViewChange={controls.setView}
                sortOptions={AUDIENCE_SORT_OPTIONS}
                sort={controls.sort}
                onSortChange={(next) => controls.setSort(next as SortState<AudienceSortKey>)}
                defaultSort={AUDIENCE_DEFAULT_SORT}
                onClearAll={controls.clearAll}
                totalCount={items.length}
                resultCount={visible.length}
                itemNoun={{ singular: 'audience', plural: 'audiences' }}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden">
            {visible.length === 0 ? (
              <ListNoMatches
                noun="audiences"
                onClear={() => {
                  controls.setQuery('');
                  controls.clearFilters();
                }}
              />
            ) : controls.view === 'list' ? (
              <div className="mx-auto h-full max-w-5xl px-6 py-4">
                <Table
                  data={visible}
                  columns={columns}
                  getRowId={getItemKey}
                  onRowClick={openAudience}
                  fixedLayout
                  fillHeight
                  emptyState="No audiences"
                />
              </div>
            ) : (
              <VirtualizedCards
                items={visible}
                getItemKey={getItemKey}
                minColumnRem={20}
                maxColumns={3}
                maxContentRem={64}
                estimateRowHeight={148}
                className="h-full"
                renderItem={renderCard}
              />
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={deletingId !== null}
        onOpenChange={(next) => {
          if (!next) setDeletingId(null);
        }}
        title="Delete this audience?"
        description={
          <>
            This permanently deletes {deletingAudience ? `"${deletingAudience.title}"` : 'the audience'}. The
            conversation that created it is kept. This can&apos;t be undone.
          </>
        }
        cancelLabel="Keep it"
        confirmLabel="Delete"
        loading={deleteMutation.isPending}
        onConfirm={() => deletingId && deleteMutation.mutate({ id: deletingId })}
      />
    </div>
  );
}
